import React, { useState, useEffect } from "react";
import "./NewCamp.scss";
import { AlertService, http } from "../fx-core";

export default function NewCamp() {
  // Helper functions for default values
  const formatDateTimeLocal = (date) => {
    const pad = (num) => num.toString().padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth()+1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  };

  const getTempCampaignName = () => {
    const now = new Date();
    return `TempCampaign-${now.getTime()}`;
  };

  const getTempStartDateTime = () => {
    return formatDateTimeLocal(new Date());
  };

  const getTempEndDateTime = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return formatDateTimeLocal(tomorrow);
  };

  // States
  const [filters, setFilters] = useState({
    minimumPrice: "",
    maximumPrice: ""
  });

  const [products, setProducts] = useState([]);
  const [areas, setAreas] = useState([]);
  const [storefronts, setStorefronts] = useState([]);
  const [productItems, setProductItems] = useState([]);
  
  // Selected values
  const [selectedArea, setSelectedArea] = useState("");
  const [selectedStorefront, setSelectedStorefront] = useState("");
  const [selectedProduct, setSelectedProduct] = useState("");
  const [quantity, setQuantity] = useState("");
  const [price, setPrice] = useState("");
  
  // Campaign details (user will enter these before submit)
  const [campaignName, setCampaignName] = useState("");
  const [startDateTime, setStartDateTime] = useState("");
  const [endDateTime, setEndDateTime] = useState("");
  
  // Table data and created campaign IDs
  const [tableData, setTableData] = useState([]);
  const [createdCampaignIds, setCreatedCampaignIds] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Handle input field changes
  const handleFilter = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  // Fetch filtered products
  const fetchProducts = () => {
    const min = parseFloat(filters.minimumPrice);
    const max = parseFloat(filters.maximumPrice);

    if (!min || !max) {
      AlertService.showErrorMsg("Please enter both minimum and maximum prices");
      return;
    }

    if (min >= max) {
      return AlertService.showErrorMsg("Maximum price should be greater than minimum price");
    }

    http.post({
      url: "treasurehunt/filtered",
      data: { minPrice: min, maxPrice: max }
    }).then((res) => {
      const result = res.data;
      setProducts(result);
      setAreas(result.map(item => ({
        id: item.communityId,
        name: item.communityName
      })));
    }).catch(() => {
      AlertService.showErrorMsg("Failed to fetch products");
    });
  };

  // Update storefronts when area changes
  useEffect(() => {
    if (selectedArea && products.length) {
      const area = products.find(a => a.communityId.toString() === selectedArea);
      if (area) {
        setStorefronts(area.storefronts.map(s => ({
          id: s.stallId,
          name: s.stallName
        })));
        setSelectedStorefront("");
        setProductItems([]);
      }
    }
  }, [selectedArea, products]);

  // Update products when storefront changes
  useEffect(() => {
    if (selectedArea && selectedStorefront && products.length) {
      const area = products.find(a => a.communityId.toString() === selectedArea);
      if (area) {
        const store = area.storefronts.find(s => s.stallId.toString() === selectedStorefront);
        if (store) {
          setProductItems(store.products.map(p => ({
            id: p.itemId,
            name: p.itemName,
            price: p.price,
            quantity: p.quantity
          })));
          setSelectedProduct("");
        }
      }
    }
  }, [selectedStorefront, selectedArea, products]);

  // Auto-fill price and quantity when product selected
  useEffect(() => {
    if (selectedProduct && productItems.length) {
      const product = productItems.find(p => p.id.toString() === selectedProduct);
      if (product) {
        setPrice(product.price);
        setQuantity(product.quantity);
      }
    }
  }, [selectedProduct, productItems]);

  // Add product to campaign (using temporary campaign details)
  const addProduct = async () => {
    if (!selectedArea || !selectedStorefront || !selectedProduct || !quantity || !price) {
      AlertService.showErrorMsg("Please fill all product fields");
      return;
    }

    const area = areas.find(a => a.id.toString() === selectedArea);
    const store = storefronts.find(s => s.id.toString() === selectedStorefront);
    const product = productItems.find(p => p.id.toString() === selectedProduct);

    if (!area || !store || !product) {
      AlertService.showErrorMsg("Invalid selection");
      return;
    }

    setIsSubmitting(true);

    try {
      // Use temporary campaign details for adding products
      const tempCampaignName = getTempCampaignName();
      const tempStartTime = getTempStartDateTime();
      const tempEndTime = getTempEndDateTime();

      const response = await http.post({
        url: "treasurehunt/add",
        data: {
          CampaignName: tempCampaignName,
          CommunityId: area.id,
          CommunityName: area.name,
          StallId: store.id,
          StallName: store.name,
          ItemId: product.id,
          ItemName: product.name,
          Quantity: quantity,
          Price: price,
          Status: "Scheduled",
          StartDateTime: tempStartTime,
          EndDateTime: tempEndTime
        }
      });

      if (response.data.success) {
        const newCampaignId = response.data.id;
        setCreatedCampaignIds([...createdCampaignIds, newCampaignId]);
        
        setTableData([...tableData, {
          areaId: area.id,
          areaName: area.name,
          stallId: store.id,
          stallName: store.name,
          itemId: product.id,
          itemName: product.name,
          quantity: quantity,
          price: price,
          status: "Scheduled",
          winner: "TBD",
          campaignId: newCampaignId
        }]);

        // Reset product fields
        setSelectedProduct("");
        setQuantity("");
        setPrice("");

        AlertService.showSuccessMsg("Product added successfully!");
      }
    } catch (err) {
      AlertService.showErrorMsg("Failed to add product");
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Delete product from campaign
  const deleteProduct = async (index, campaignId) => {
    try {
      await http.delete(`treasurehunt/product/${campaignId}`);
      
      const newTableData = [...tableData];
      newTableData.splice(index, 1);
      setTableData(newTableData);
      
      const newCampaignIds = [...createdCampaignIds];
      newCampaignIds.splice(index, 1);
      setCreatedCampaignIds(newCampaignIds);
      
      AlertService.showSuccessMsg("Product removed successfully");
    } catch (err) {
      AlertService.showErrorMsg("Failed to remove product");
      console.error(err);
    }
  };

  // Submit final campaign with user-entered details
const submitCampaign = async () => {
  if (!campaignName || !startDateTime || !endDateTime) {
    AlertService.showErrorMsg("Please enter campaign name and dates");
    return;
  }

  if (createdCampaignIds.length === 0) {
    AlertService.showErrorMsg("Please add at least one product");
    return;
  }

  setIsSubmitting(true);

  try {
    const response = await http.post({
      url: "treasurehunt/submit",
      data: {
        campaignName: campaignName.trim(),
        startDate: startDateTime,
        endDate: endDateTime,
        campaignIds: createdCampaignIds
      }
    });

    AlertService.showSuccessMsg(`Campaign submitted!`);

    // Reset form
    setCampaignName("");
    setStartDateTime("");
    setEndDateTime("");
    setTableData([]);
    setCreatedCampaignIds([]);

  } catch (err) {
  const errorMsg = err?.response?.data?.message || err?.response?.data || "Failed to submit campaign";

  if (errorMsg.includes("Start date cannot be in the past")) {
    AlertService.showErrorMsg("Start date cannot be in the past.");
  } else if (errorMsg.includes("End date must be after start date")) {
    AlertService.showErrorMsg("End date must be after start date.");
  } else if (errorMsg.includes("Conflicts with existing campaign")) {
    AlertService.showErrorMsg("This date is already taken. Please choose another date.");
  } else {
    AlertService.showErrorMsg(errorMsg);
  }

  console.error("Campaign Submit Error:", err);
}
finally {
    setIsSubmitting(false);
  }
};

  return (
    <div className="new-camp-container">
      <div className="filters">
        <div className="filter-group">
          <select>
            <option value="">Selection Criteria</option>
            <option value="price">Price</option>
          </select>
        </div>
        <div className="filter-group">
          <input
            type="number"
            name="minimumPrice"
            placeholder="Min"
            value={filters.minimumPrice}
            onChange={handleFilter}
          />
        </div>
        <div className="filter-group">
          <input
            type="number"
            name="maximumPrice"
            placeholder="Max"
            value={filters.maximumPrice}
            onChange={handleFilter}
          />
        </div>
        <button className="fetch-btn" onClick={fetchProducts}>
          Fetch
        </button>
      </div>

      <div className="dropdowns">
        <select 
          value={selectedArea} 
          onChange={(e) => setSelectedArea(e.target.value)}
        >
          <option value="">Area</option>
          {areas.map(area => (
            <option key={area.id} value={area.id}>{area.name}</option>
          ))}
        </select>
        
        <select 
          value={selectedStorefront} 
          onChange={(e) => setSelectedStorefront(e.target.value)}
          disabled={!selectedArea}
        >
          <option value="">Storefront</option>
          {storefronts.map(store => (
            <option key={store.id} value={store.id}>{store.name}</option>
          ))}
        </select>
        
        <select 
          value={selectedProduct} 
          onChange={(e) => setSelectedProduct(e.target.value)}
          disabled={!selectedStorefront}
        >
          <option value="">Product</option>
          {productItems.map(product => (
            <option key={product.id} value={product.id}>{product.name}</option>
          ))}
        </select>
        
        <div className="filter-group">
          <input 
            type="number" 
            placeholder="Quantity" 
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
          />
        </div>
        
        <div className="filter-group">
          <input 
            type="number" 
            placeholder="Price" 
            value={price}
            onChange={(e) => setPrice(e.target.value)}
          />
        </div>
        
        <button 
          className="add-btn" 
          onClick={addProduct}
          disabled={isSubmitting}
        >
          {isSubmitting ? "Adding..." : "+ Add"}
        </button>
      </div>

      <table className="campaign-table">
        <thead>
          <tr>
            <th>Area</th>
            <th>Storefront</th>
            <th>Product</th>
            <th>Quantity</th>
            <th>Price</th>
            <th>Status</th>
            <th>Action</th>
            <th>Winner</th>
          </tr>
        </thead>
        <tbody>
          {tableData.map((row, index) => (
            <tr key={index}>
              <td>{row.areaName}</td>
              <td>{row.stallName}</td>
              <td>{row.itemName}</td>
              <td>{row.quantity}</td>
              <td>{row.price}</td>
              <td>{row.status}</td>
              <td>
                <button 
                  className="delete-btn"
                  onClick={() => deleteProduct(index, row.campaignId)}
                  disabled={isSubmitting}
                >
                  🗑️
                </button>
              </td>
              <td>{row.winner}</td>
            </tr>
          ))}
          {tableData.length === 0 && (
            <tr>
              <td colSpan="8">No products added yet</td>
            </tr>
          )}
        </tbody>
      </table>

      <div className="campaign-footer">
        <input 
          type="text" 
          placeholder="Campaign Name" 
          value={campaignName}
          onChange={(e) => setCampaignName(e.target.value)}
          required
        />
        <input 
          type="datetime-local" 
          placeholder="Start time" 
          value={startDateTime}
          onChange={(e) => setStartDateTime(e.target.value)}
          required
        />
        <input 
          type="datetime-local" 
          placeholder="End time" 
          value={endDateTime}
          onChange={(e) => setEndDateTime(e.target.value)}
          required
        />
        
        <button 
          className="submit-btn" 
          onClick={submitCampaign}
          disabled={isSubmitting || createdCampaignIds.length === 0}
        >
          {isSubmitting ? "Submitting..." : "Submit Campaign"}
        </button>
      </div>
    </div>
  );
}

